/* eslint-disable camelcase */
/**
 * Root interface for the x-auth-core module
 */
import { XV1App, AuthConfig, AuthToken, AuthContext, XV2App, CrossDomainApp, App, IInteractionEventData } from '@nab/x-types';

export { AuthConfig, AuthToken, AuthContext } from '@nab/x-types';

export interface ModifyCacheFn<C extends AuthConfig = AuthConfig> {
    (token: AuthToken<C>): Promise<void>;
}

export interface AuthStrategy<T extends AuthConfig = AuthConfig> {
    // Must acquire a token on-demand every time without any caching logic
    authenticate(config: T): Promise<AuthToken<T>>;

    // This function should return true if your auth strategy is capable of acquiring a token for the supplied config
    supports(config: T): boolean;
}

export interface TokenCacheStrategy<C extends AuthConfig = AuthConfig> {
    // Add a token to the cache
    addToken: ModifyCacheFn<C>;

    // Retrieve a token from the cache - should resolve with undefined if no token is available
    getToken(config: C): Promise<AuthToken<C>>;

    // Purge a token from the cache
    removeToken: ModifyCacheFn<C>;

    // Retrieve all cache entry
    getEntryCache?(): CacheEntry[];
}

export interface DafScopeConfig {
    context: AuthContext;
    clientId?: string;
    scopes?: string | string[];
}

export enum DafLoginType {
    USER_LOGIN = 'UserLogin'
}

export interface DafFlags {
    stepUp?: '2FA' | string; // can be any value to identify acr2 to acr3 or acr3 to acr4, etc.
    loginType?: DafLoginType.USER_LOGIN | string;
}

export interface DafAuthConfig<APP extends App = XV1App | XV2App> extends AuthConfig<APP>, DafScopeConfig {
    appMetaData?: APP;
    grantType?: DafGrantType;
    isSeed?: boolean;
    flags?: DafFlags;
}

export interface DafEventBasedAuthConfig<APP extends App = XV1App | XV2App> extends DafAuthConfig<APP> {
    eventData?: IInteractionEventData;
}

export interface DafClientCredentialsAuthConfig extends DafAuthConfig {
    username?: string;
    password?: string; // Note this gets removed once login is complete
}

export interface DafSeedAuthConfig extends DafAuthConfig {
    isSeed?: true;
}

export interface DafAuthToken<C extends DafAuthConfig = DafAuthConfig> extends AuthToken<C> {
    idleTimeout?: number; // Number of milli's this token remains valid for without use
    issuedAt?: number; // Time of issue
    expires?: number; // Expiry time
}

export interface DafAnonymousAuthConfig extends DafAuthConfig {
    tokenType: 'Bearer';
    grantType: DafGrantType.nabAnonymous;
}

export type DafExchangeAuthConfig = DafAuthConfig<XV1App | XV2App | CrossDomainApp>;

export type DafExchangeAuthToken = DafAuthToken<DafExchangeAuthConfig>;

export type DafAnonymousAuthToken = DafAuthToken<DafAnonymousAuthConfig>;

export enum DafGrantType {
    nabAnonymous = 'nab:anonymous',
    clientCredentials = 'client_credentials',
    tokenExchange = 'urn:ietf:params:oauth:grant-type:token-exchange',
    refreshToken = 'refresh_token',
    AuthorizationCode = 'authorization_code',
    Password = 'password',
    RefreshToken = 'refresh_token',
    ClientCredentials = 'client_credentials',
    UsernamePassword = 'nab:usernamePassword',
    DeviceRegIdPasscode = 'nab:deviceRegIdPasscode',
    DeviceRegIdPassword = 'nab:deviceRegIdPassword',
    DeviceRegIdFingerprint = 'nab:deviceRegIdFingerprint',
    DeviceRegIdFaceprint = 'nab:deviceRegIdFaceprint',
    IvrPasscode = 'nab:ivrPasscode',
    UsernameTokenCode = 'nab:usernameTokenCode',
    UsernamePasswordTokenCode = 'nab:usernamePasswordTokenCode',
    UsernameOTP = 'nab:usernameOTP',
    UsernamePasswordOTP = 'nab:usernamePasswordOTP',
    Saml2Bearer = 'urn:ietf:params:oauth:grant-type:saml2-bearer',
    JwtBearer = 'urn:ietf:params:oauth:grant-type:jwt-bearer',
    TokenExchange = 'urn:ietf:params:oauth:grant-type:token-exchange',
    Anonymous = 'nab:anonymous',
    IvrInAppCall = 'nab:ivrInAppCall',
    WssePasswordDigest = 'nab:wssePasswordDigest',
    Reauthentication = 'nab:reauthentication'
}

export enum DafAuthError {
    UNAUTHENTICATED = 'UNAUTHENTICATED',
    INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
    UNAUTHORISED = 'UNAUTHORISED',
    UNKNOWN_ERROR = 'UNKNOWN_ERROR',
    TPS_LIMIT_REACHED = 'TPS_LIMIT_REACHED',
    GENERAL_ERROR = 'GENERAL_ERROR',
    ILLEGAL_ARGUMENT = 'ILLEGAL_ARGUMENT',
    BAD_REQUEST = 'BAD_REQUEST'
}

export interface DafAuthResponse {
    access_token: string;
    token_type: string;
    expires_in?: number;
    idleTimeout?: number;
}

export enum TOKEN_SOURCE {
    DAF = 'DAF',
    MOBILE = 'mobile',
    NATIVE = 'Native'
}

export enum TransactionStepupAuthError {
    TRANSACTION_STEPUP_REJECTED_ERROR = 'transaction_stepup_rejected'
}

export enum CheckSessionEvent {
    IN_PROGRESS = 'checking_session_inprogress',
    SUCCESS = 'checking_session_success'
}

export enum SessionStatusResponse {
    ACCEPT = 'ACCEPT',
    REJECT = 'REJECT',
    FAILED = 'FAILED',
    STEPUP_REQUIRED = 'STEPUP_REQUIRED'
}

export interface CacheEntry {
    parentTokenValue?: string;
    token: AuthToken<DafAuthConfig>;
}
